/* Admin support queue + thread view.
 *
 * Mounted at:
 *   /app/admin/support           → queue/list
 *   /app/admin/support/:id       → single ticket thread + admin controls
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SupportThemesWidget from "@/components/SupportThemesWidget";
import {
  ArrowLeft, Send, Loader2, MessageSquare, AlertTriangle,
  CheckCircle2, Clock, AlertCircle, ShieldCheck, FileText,
  TrendingUp, Mail, ExternalLink, Lock, Flag, Sparkles,
} from "lucide-react";
import { STATUS_META, CATEGORY_LABEL } from "@/pages/app/Support";

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "waiting_user", label: "Waiting on User" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const PRIORITY_COLOR = {
  low: "text-stone-500", normal: "text-sky-600",
  high: "text-amber-600", urgent: "text-rose-600",
};


export default function AdminSupport() {
  const { ticketId } = useParams();
  if (ticketId) return <AdminThread ticketId={ticketId} />;
  return <AdminQueue />;
}


function AdminQueue() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "all", category: "all", q: "" });
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const [list, s] = await Promise.all([
        api.get("/admin/support/tickets", {
          params: {
            status: filter.status === "all" ? undefined : filter.status,
            category: filter.category === "all" ? undefined : filter.category,
            q: filter.q || undefined,
          },
        }),
        api.get("/admin/support/stats"),
      ]);
      setTickets(list.data?.items || []);
      setStats(s.data || null);
    } catch {
      toast.error("Couldn't load queue.");
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-support-queue">
      <div>
        <h1 className="font-display font-extrabold text-2xl">Support Queue</h1>
        <p className="text-sm text-[var(--ink-2)] mt-1">
          Every ticket lands here. Aim for first-touch under 24h.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Open" value={stats.open} tone="amber" />
          <StatCard label="In Progress" value={stats.in_progress} tone="sky" />
          <StatCard label="Waiting on User" value={stats.waiting_user} tone="violet" />
          <StatCard label="Aging > 24h" value={stats.aging_24h} tone={stats.aging_24h > 0 ? "rose" : "stone"} icon={AlertTriangle} />
        </div>
      )}

      {/* Filters */}
      <div className="card p-3 flex flex-wrap items-center gap-2">
        <select
          value={filter.status}
          onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
          className="input text-sm py-1.5"
          data-testid="admin-support-filter-status"
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select
          value={filter.category}
          onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}
          className="input text-sm py-1.5"
          data-testid="admin-support-filter-category"
        >
          <option value="all">All categories</option>
          {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <input
          type="text" value={filter.q}
          onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
          placeholder="Search subject, email, ref…"
          className="input text-sm py-1.5 flex-1 min-w-[180px]"
          data-testid="admin-support-filter-q"
        />
        <button
          type="button"
          onClick={async () => {
            try {
              const { data } = await api.post("/admin/support/pulse/send-now");
              if (data?.ok) toast.success(`Pulse sent to ${data.sent_to}`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Couldn't send pulse.");
            }
          }}
          className="text-xs font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-violet-50 transition"
          data-testid="admin-support-pulse-send-now"
          title="Send the weekly Support Pulse digest to your email right now"
        >
          <Sparkles size={12} /> Send pulse now
        </button>
      </div>

      <SupportThemesWidget />

      {/* Queue */}
      {loading ? (
        <div className="card p-8 text-center text-[var(--ink-2)]">
          <Loader2 size={18} className="animate-spin inline mr-2" /> Loading…
        </div>
      ) : tickets.length === 0 ? (
        <div className="card p-10 text-center">
          <ShieldCheck size={36} className="mx-auto text-[var(--ink-3)] mb-2" />
          <div className="font-display font-bold">All clear</div>
          <p className="text-sm text-[var(--ink-2)] mt-1">No tickets match these filters.</p>
        </div>
      ) : (
        <div className="card divide-y divide-[var(--line)]">
          {tickets.map((t) => (
            <button
              key={t.id} type="button"
              onClick={() => nav(`/v3/admin/console/support/${t.id}`)}
              className={`w-full text-left p-4 hover:bg-stone-50 transition flex items-start gap-3 ${
                t.unread_by_admin ? "bg-amber-50/40" : ""
              }`}
              data-testid={`admin-support-row-${t.id}`}
            >
              <StatusBadge status={t.status} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-semibold text-sm ${t.unread_by_admin ? "text-[var(--ink)]" : ""}`}>
                    {t.subject}
                  </span>
                  {t.unread_by_admin && (
                    <span className="bg-rose-500 text-white text-[9px] font-bold uppercase px-1.5 py-0.5 rounded">
                      Unread
                    </span>
                  )}
                  <span className={`text-[10px] font-bold uppercase ${PRIORITY_COLOR[t.priority] || ""}`}>
                    {t.priority !== "normal" && <><Flag size={9} className="inline" /> {t.priority}</>}
                  </span>
                </div>
                <div className="text-xs text-[var(--ink-2)] mt-1 flex items-center gap-2 flex-wrap">
                  <span className="font-mono">{t.ref}</span>
                  <span>·</span>
                  <span>{CATEGORY_LABEL[t.category] || t.category}</span>
                  {t.ai_triaged_at && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[9px] font-bold uppercase tracking-wider" title={t.triage_note || "AI-triaged"}>
                      <Sparkles size={9} /> AI
                    </span>
                  )}
                  <span>·</span>
                  <span>{t.user_name || t.user_email}</span>
                  <span className="text-[var(--ink-3)]">({t.user_tier})</span>
                  <span>·</span>
                  <span>{new Date(t.updated_at).toLocaleString()}</span>
                </div>
                {t.triage_note && (
                  <div className="text-[11px] text-violet-700 italic mt-1 truncate" data-testid={`admin-ticket-triage-note-${t.id}`}>
                    {t.triage_note}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function StatCard({ label, value, tone, icon: Icon }) {
  const palette = {
    amber: "border-amber-200 bg-amber-50",
    sky: "border-sky-200 bg-sky-50",
    violet: "border-violet-200 bg-violet-50",
    rose: "border-rose-300 bg-rose-50",
    stone: "border-stone-200 bg-stone-50",
  }[tone] || "border-stone-200 bg-stone-50";
  return (
    <div className={`border rounded-lg p-3 ${palette}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--ink-2)] flex items-center gap-1">
        {Icon && <Icon size={10} />} {label}
      </div>
      <div className="font-display font-extrabold text-2xl mt-1">{value}</div>
    </div>
  );
}


function AdminThread({ ticketId }) {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [mode, setMode] = useState("reply"); // reply | note
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const scrollRef = useRef(null);

  const suggestReply = async () => {
    if (suggesting) return;
    setSuggesting(true);
    try {
      const { data } = await api.post(
        `/admin/support/tickets/${ticketId}/suggest-reply`,
        { hint: "" },
      );
      if (data?.draft) {
        setReply(data.draft);
        setMode("reply");
        toast.success("Draft loaded — edit before sending.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate a draft.");
    } finally {
      setSuggesting(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/support/tickets/${ticketId}`);
      setTicket(data?.ticket || null);
      setMessages(data?.messages || []);
    } catch {
      toast.error("Couldn't load ticket.");
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const body = mode === "note" ? noteBody : reply;
    if (body.trim().length < 1) return;
    setSending(true);
    try {
      if (mode === "note") {
        await api.post(`/admin/support/tickets/${ticketId}/note`, { body });
        setNoteBody("");
      } else {
        await api.post(`/admin/support/tickets/${ticketId}/reply`, { body });
        setReply("");
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send.");
    } finally {
      setSending(false);
    }
  };

  const patch = async (patch) => {
    try {
      const { data } = await api.patch(`/admin/support/tickets/${ticketId}`, patch);
      setTicket(data);
      toast.success("Updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update.");
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-[var(--ink-2)]">
      <Loader2 size={18} className="animate-spin inline mr-2" /> Loading…
    </div>;
  }
  if (!ticket) {
    return (
      <div className="card p-8 text-center">
        <div className="font-display font-bold">Ticket not found</div>
        <Link to="/v3/admin/console/support" className="text-emerald-600 text-sm font-semibold mt-2 inline-block">
          ← Back to queue
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-support-thread">
      <Link to="/v3/admin/console/support" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)] inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Back to queue
      </Link>

      {/* Header card */}
      <div className="card p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="font-display font-extrabold text-xl">{ticket.subject}</h1>
            <div className="text-xs text-[var(--ink-2)] mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-mono">{ticket.ref}</span>
              <span>·</span>
              <span>{CATEGORY_LABEL[ticket.category] || ticket.category}</span>
              <span>·</span>
              <span>Opened {new Date(ticket.created_at).toLocaleString()}</span>
            </div>
            <div className="mt-3 text-sm">
              <span className="text-[var(--ink-2)]">From:</span>{" "}
              <strong>{ticket.user_name || "—"}</strong>{" "}
              <a href={`mailto:${ticket.user_email}`} className="text-emerald-700 hover:underline ml-1 inline-flex items-center gap-0.5">
                {ticket.user_email} <Mail size={10} />
              </a>
              <span className="ml-2 text-[10px] uppercase font-bold tracking-wider text-[var(--ink-3)] border border-stone-200 px-1.5 py-0.5 rounded">
                {ticket.user_tier} tier
              </span>
            </div>
            {ticket.page_url && (
              <div className="text-xs text-[var(--ink-3)] mt-1.5">
                <ExternalLink size={10} className="inline mr-0.5" />
                Reported from <code className="bg-stone-100 px-1 rounded">{ticket.page_url}</code>
              </div>
            )}
          </div>
        </div>

        {/* Admin controls */}
        <div className="mt-4 pt-4 border-t border-[var(--line)] flex flex-wrap gap-2 items-center">
          <label className="text-xs font-bold uppercase text-[var(--ink-2)]">Status:</label>
          <select
            value={ticket.status}
            onChange={(e) => patch({ status: e.target.value })}
            className="input text-xs py-1"
            data-testid="admin-thread-status"
          >
            {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <label className="text-xs font-bold uppercase text-[var(--ink-2)] ml-3">Priority:</label>
          <select
            value={ticket.priority}
            onChange={(e) => patch({ priority: e.target.value })}
            className={`input text-xs py-1 ${PRIORITY_COLOR[ticket.priority] || ""}`}
            data-testid="admin-thread-priority"
          >
            {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>

      {/* Thread */}
      <div className="card p-0 overflow-hidden">
        <div ref={scrollRef} className="max-h-[55vh] overflow-y-auto px-5 py-4 space-y-4">
          {messages.map((m) => (
            <AdminMessageBubble key={m.id} msg={m} />
          ))}
        </div>

        {/* Reply / note tabs */}
        <div className="border-t border-[var(--line)] bg-stone-50 px-3 pt-2">
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setMode("reply")}
              className={`px-3 py-1.5 text-xs font-bold rounded-t-md ${
                mode === "reply"
                  ? "bg-white border-t border-l border-r border-[var(--line)] text-[var(--ink)]"
                  : "text-[var(--ink-2)] hover:text-[var(--ink)]"
              }`}
              data-testid="admin-thread-mode-reply"
            >
              <MessageSquare size={11} className="inline mr-1" /> Reply to user
            </button>
            <button
              type="button"
              onClick={() => setMode("note")}
              className={`px-3 py-1.5 text-xs font-bold rounded-t-md ${
                mode === "note"
                  ? "bg-amber-100 border-t border-l border-r border-amber-200 text-amber-900"
                  : "text-[var(--ink-2)] hover:text-[var(--ink)]"
              }`}
              data-testid="admin-thread-mode-note"
            >
              <Lock size={11} className="inline mr-1" /> Internal note
            </button>
          </div>
        </div>
        <form onSubmit={send} className={`p-4 ${mode === "note" ? "bg-amber-50" : "bg-white"}`}>
          {mode === "reply" ? (
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={4}
              placeholder="Reply to the user (they'll get an email)…"
              className="input w-full resize-none"
              data-testid="admin-thread-reply-textarea"
            />
          ) : (
            <textarea
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
              rows={4}
              placeholder="Internal note — only admins see this. Use for next-steps, observations, what you tried."
              className="input w-full resize-none border-amber-300 focus:border-amber-400"
              data-testid="admin-thread-note-textarea"
            />
          )}
          <div className="flex justify-between items-center mt-2 gap-2">
            {mode === "reply" ? (
              <button
                type="button"
                onClick={suggestReply}
                disabled={suggesting || sending}
                className="text-xs font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="admin-thread-suggest-reply"
                title="Draft a reply with AI — you can edit before sending."
              >
                {suggesting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {suggesting ? "Drafting…" : "Suggest reply (AI)"}
              </button>
            ) : <span />}
            <button
              type="submit"
              disabled={sending}
              className={`font-bold text-sm px-4 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-60 ${
                mode === "note"
                  ? "bg-amber-600 hover:bg-amber-700 text-white"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"
              }`}
              data-testid="admin-thread-send"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {mode === "note" ? "Save note" : "Send reply"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function AdminMessageBubble({ msg }) {
  const isInternal = msg.is_internal_note;
  const isAdmin = msg.author_kind === "admin";

  if (isInternal) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid={`admin-msg-${msg.id}`}>
        <div className="text-[11px] text-amber-800 font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
          <Lock size={10} /> Internal note · {msg.author_name}
          <span className="text-amber-700 font-normal normal-case tracking-normal ml-2">
            {new Date(msg.created_at).toLocaleString()}
          </span>
        </div>
        <div className="text-sm whitespace-pre-wrap text-amber-950">{msg.body}</div>
      </div>
    );
  }

  return (
    <div className={`flex ${isAdmin ? "justify-start" : "justify-end"}`} data-testid={`admin-msg-${msg.id}`}>
      <div className="max-w-[85%]">
        <div className={`text-[11px] mb-1 ${isAdmin ? "text-emerald-700 font-bold" : "text-[var(--ink-3)] text-right"}`}>
          {isAdmin ? `${msg.author_name} · Admin` : msg.author_name}
          <span className="font-normal text-[var(--ink-3)] ml-1.5">
            {new Date(msg.created_at).toLocaleString()}
          </span>
        </div>
        <div className={`rounded-xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
          isAdmin
            ? "bg-emerald-50 border border-emerald-200 text-[var(--ink)]"
            : "bg-stone-100 border border-stone-200 text-[var(--ink)]"
        }`}>
          {msg.body}
        </div>
      </div>
    </div>
  );
}


function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.open;
  return (
    <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded border text-[10px] font-bold uppercase tracking-wide ${meta.color}`}>
      <meta.Icon size={10} className={status === "in_progress" ? "animate-spin" : ""} />
      {meta.label}
    </span>
  );
}
